import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { pendingOrdersApiService } from '../../services/pendingOrdersApiService';
import {
  TransformedPendingOrder,
  ApiOrderSummaryStats,
  PendingOrdersApiState
} from '../../types/pendingOrdersApi';
import toast from 'react-hot-toast';

// Async thunks
export const fetchPendingOrders = createAsyncThunk(
  'pendingOrdersApi/fetchPendingOrders',
  async (_, { rejectWithValue }) => {
    try {
      console.log('📞 Redux: Dispatching API call to fetch pending orders...');
      
      const result = await pendingOrdersApiService.getPendingOrders();
      
      if (!result.success) {
        console.error('❌ Redux: API call failed:', result.error);
        return rejectWithValue(result.error || 'Failed to fetch pending orders');
      }

      console.log('✅ Redux: API call successful, received', result.data.length, 'orders');
      return result;
    } catch (error: any) {
      console.error('❌ Redux: Exception in fetchPendingOrders:', error);
      return rejectWithValue(error.message || 'Failed to fetch pending orders');
    }
  }
);

export const fetchOrderById = createAsyncThunk(
  'pendingOrdersApi/fetchOrderById',
  async (orderId: string, { rejectWithValue }) => {
    try {
      const order = await pendingOrdersApiService.getOrderById(orderId);
      if (!order) {
        return rejectWithValue('Order not found');
      }
      return order;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch order details');
    }
  }
);

export const fetchEscalationReasons = createAsyncThunk(
  'pendingOrdersApi/fetchEscalationReasons',
  async (_, { rejectWithValue }) => {
    try {
      const reasons = await pendingOrdersApiService.getEscalationReasons();
      return reasons;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch escalation reasons');
    }
  }
);

export const fetchEscalationRecipients = createAsyncThunk(
  'pendingOrdersApi/fetchEscalationRecipients',
  async (_, { rejectWithValue }) => {
    try {
      const recipients = await pendingOrdersApiService.getEscalationRecipients();
      return recipients;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch escalation recipients');
    }
  }
);

// Initial state
const initialState: PendingOrdersApiState = {
  orders: [],
  filteredOrders: [],
  selectedOrder: null,
  filters: {},
  summaryStats: {
    totalPending: 0,
    lateOrders: 0,
    escalated: 0,
    averageWaitTime: 0,
    totalRevenue: 0,
    completionRate: 0
  },
  escalationReasons: [],
  escalationRecipients: [],
  loading: false,
  error: null,
  lastUpdated: new Date().toISOString()
};

// Helper function to apply filters
const applyFilters = (orders: TransformedPendingOrder[], filters: PendingOrdersApiState['filters']): TransformedPendingOrder[] => {
  let filtered = [...orders];

  if (filters.status?.length) {
    filtered = filtered.filter(order => filters.status!.includes(order.status));
  }

  if (filters.priority?.length) {
    filtered = filtered.filter(order => filters.priority!.includes(order.priority));
  }

  if (filters.restaurant?.length) {
    filtered = filtered.filter(order => filters.restaurant!.includes(order.restaurant.id));
  }

  if (filters.customer) {
    const searchTerm = filters.customer.toLowerCase();
    filtered = filtered.filter(order => 
      order.customer.name.toLowerCase().includes(searchTerm) ||
      order.customer.phone.toLowerCase().includes(searchTerm) ||
      order.orderNumber.toLowerCase().includes(searchTerm)
    );
  }

  if (filters.isEscalated !== undefined) {
    filtered = filtered.filter(order => order.isEscalated === filters.isEscalated);
  }

  if (filters.waitTimeRange) {
    filtered = filtered.filter(order => 
      order.waitingTime >= filters.waitTimeRange!.min &&
      order.waitingTime <= filters.waitTimeRange!.max
    );
  }

  if (filters.paymentStatus?.length) {
    filtered = filtered.filter(order => 
      filters.paymentStatus!.includes(order.paymentStatus)
    );
  }

  if (filters.orderType?.length) {
    filtered = filtered.filter(order => 
      filters.orderType!.includes(order.orderType)
    );
  }

  if (filters.dateRange) {
    const startDate = new Date(filters.dateRange.start);
    const endDate = new Date(filters.dateRange.end);
    filtered = filtered.filter(order => {
      const orderDate = new Date(order.createdAt);
      return orderDate >= startDate && orderDate <= endDate;
    });
  }

  return filtered;
};

// Slice
const pendingOrdersApiSlice = createSlice({
  name: 'pendingOrdersApi',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<PendingOrdersApiState['filters']>) => {
      console.log('🔧 Redux: Setting filters:', action.payload);
      state.filters = action.payload;
      state.filteredOrders = applyFilters(state.orders, action.payload);
    },
    clearFilters: (state) => {
      console.log('🧹 Redux: Clearing filters');
      state.filters = {};
      state.filteredOrders = [...state.orders];
    },
    selectOrder: (state, action: PayloadAction<TransformedPendingOrder | null>) => {
      state.selectedOrder = action.payload;
    },
    updateOrderInList: (state, action: PayloadAction<TransformedPendingOrder>) => {
      const index = state.orders.findIndex(order => order.id === action.payload.id);
      if (index !== -1) {
        state.orders[index] = action.payload;
        state.filteredOrders = applyFilters(state.orders, state.filters);
      }
    },
    addOrder: (state, action: PayloadAction<TransformedPendingOrder>) => {
      state.orders.unshift(action.payload);
      state.filteredOrders = applyFilters(state.orders, state.filters);
    },
    removeOrder: (state, action: PayloadAction<string>) => {
      state.orders = state.orders.filter(order => order.id !== action.payload);
      state.filteredOrders = applyFilters(state.orders, state.filters);
    },
    setRealTimeUpdates: (state, action: PayloadAction<TransformedPendingOrder[]>) => {
      console.log('🔄 Redux: Real-time update received:', action.payload.length, 'orders');
      state.orders = action.payload;
      state.filteredOrders = applyFilters(action.payload, state.filters);
      state.lastUpdated = new Date().toISOString();
      
      // Recalculate stats
      const totalPending = action.payload.filter(o => 
        ['pending', 'normal', 'flagged', 'escalated'].includes(o.status)
      ).length;
      
      const lateOrders = action.payload.filter(o => 
        new Date(o.estimatedDeliveryTime) < new Date() && 
        !['delivered', 'cancelled'].includes(o.status)
      ).length;
      
      const escalated = action.payload.filter(o => o.isEscalated).length;
      
      const averageWaitTime = action.payload.length > 0 
        ? Math.round(action.payload.reduce((acc, o) => acc + o.waitingTime, 0) / action.payload.length)
        : 0;
      
      const totalRevenue = action.payload.reduce((acc, o) => acc + o.total, 0);
      
      state.summaryStats = {
        ...state.summaryStats,
        totalPending,
        lateOrders,
        escalated,
        averageWaitTime,
        totalRevenue
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    updateSummaryStats: (state, action: PayloadAction<Partial<ApiOrderSummaryStats>>) => {
      state.summaryStats = { ...state.summaryStats, ...action.payload };
    },
    sortOrders: (state, action: PayloadAction<{ field: keyof TransformedPendingOrder; direction: 'asc' | 'desc' }>) => {
      const { field, direction } = action.payload;
      
      const sortFunction = (a: TransformedPendingOrder, b: TransformedPendingOrder) => {
        let aValue = a[field];
        let bValue = b[field];

        // Handle nested objects
        if (field === 'customer') {
          aValue = a.customer.name;
          bValue = b.customer.name;
        } else if (field === 'restaurant') {
          aValue = a.restaurant.name;
          bValue = b.restaurant.name;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        if (aValue instanceof Date && bValue instanceof Date) {
          return direction === 'asc' 
            ? aValue.getTime() - bValue.getTime()
            : bValue.getTime() - aValue.getTime();
        }

        return 0;
      };

      state.filteredOrders.sort(sortFunction);
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch pending orders
      .addCase(fetchPendingOrders.pending, (state) => {
        console.log('⏳ Redux: fetchPendingOrders pending...');
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingOrders.fulfilled, (state, action) => {
        console.log('✅ Redux: fetchPendingOrders fulfilled with', action.payload.data.length, 'orders');
        state.loading = false;
        state.orders = action.payload.data;
        state.summaryStats = action.payload.stats;
        state.filteredOrders = applyFilters(action.payload.data, state.filters);
        state.lastUpdated = new Date().toISOString();
        state.error = null;
      })
      .addCase(fetchPendingOrders.rejected, (state, action) => {
        console.error('❌ Redux: fetchPendingOrders rejected:', action.payload);
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to fetch pending orders');
      })

      // Fetch order by ID
      .addCase(fetchOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedOrder = action.payload;
      })
      .addCase(fetchOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Fetch escalation reasons
      .addCase(fetchEscalationReasons.fulfilled, (state, action) => {
        state.escalationReasons = action.payload;
      })
      .addCase(fetchEscalationReasons.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // Fetch escalation recipients
      .addCase(fetchEscalationRecipients.fulfilled, (state, action) => {
        state.escalationRecipients = action.payload;
      })
      .addCase(fetchEscalationRecipients.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  selectOrder,
  updateOrderInList,
  addOrder,
  removeOrder,
  setRealTimeUpdates,
  clearError,
  updateSummaryStats,
  sortOrders
} = pendingOrdersApiSlice.actions;

export default pendingOrdersApiSlice.reducer;